// src/pages/Complaint.js

import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ZaraAssistant from "../components/ZaraAssistant";
import { useLanguage } from "../context/LanguageContext";
import translations from "../data/translations";
import { apiUrl } from "../config/apiConfig";

const COMPLAINT_TYPES = [
  { id: "wrong_deduction", labelEn: "Wrong Deduction",          labelRu: "Galat Deduction",        icon: "💸" },
  { id: "no_consent",      labelEn: "Subscribed Without Consent", labelRu: "Bina Izazat Subscribe", icon: "🚫" },
  { id: "claim_rejected",  labelEn: "Claim Rejected",           labelRu: "Claim Reject Hua",       icon: "❌" },
  { id: "slow_support",    labelEn: "Slow Support",             labelRu: "Support Dheema Tha",     icon: "🐢" },
  { id: "service_issue",   labelEn: "Service Issue",            labelRu: "Service Mein Masla",     icon: "⚙️" },
  { id: "other",           labelEn: "Other Issue",              labelRu: "Koi Aur Masla",          icon: "📝" },
];

const StepBar = ({ current, total, color }) => (
  <div className="d-flex align-items-center gap-2 mb-4">
    {Array.from({ length: total }, (_, i) => {
      const done = i + 1 < current;
      const active = i + 1 === current;
      return (
        <React.Fragment key={i}>
          <div className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
            style={{ width: 28, height: 28, background: done || active ? color : "#e6eef3", color: done || active ? "#fff" : "#aaa", fontSize: "0.72rem", fontWeight: 700, transition: "background 0.3s" }}>
            {done ? "✓" : i + 1}
          </div>
          {i < total - 1 && (
            <div style={{ flex: 1, height: 3, borderRadius: 2, background: done ? color : "#e6eef3", transition: "background 0.3s" }} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

const Complaint = () => {
  const navigate = useNavigate();
  const { service, phone, network } = useLocation().state || {};
  const [step, setStep] = useState(1);
  const [type, setType] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  const [submittedComplaintId, setSubmittedComplaintId] = useState(null);
  const [complaintId] = useState(() => "WD" + Date.now().toString().slice(-6));
  const { language } = useLanguage();
  const t = translations[language] || translations["Roman Urdu"];

  const isEn = language === "English";
  const stepTitles = [t.step1Title, t.step2Title, t.step3Title];
  const serviceName = service?.name || `${network || ""} ${t.serviceLabel || "Service"}`.trim();

  return (
    <div className="container-fluid py-4 px-3 px-md-4" style={{ maxWidth: "900px", margin: "0 auto" }}>
      <Header showBack onBack={() => step > 1 && step < 3 ? setStep(s => s - 1) : navigate(-1)} />

      <div className="card shadow-sm border-0 rounded-4 p-4 mb-4">
        <div className="d-flex align-items-center gap-3 mb-4">
          <div className="d-flex align-items-center justify-content-center rounded-circle text-white fw-bold flex-shrink-0"
            style={{ width: 42, height: 42, background: "#e67e22", fontSize: "1rem" }}>⚠️</div>
          <div>
            <p style={{ fontWeight: 700, fontSize: "1rem", color: "#1a1a2e", marginBottom: "2px" }}>{t.complaintPageTitle}</p>
            <p style={{ fontSize: "0.78rem", color: "#888", marginBottom: 0 }}>{t.complaintPageSub}</p>
          </div>
        </div>

        <StepBar current={step} total={3} color="#e67e22" />
        <p style={{ fontSize: "0.72rem", color: "#e67e22", fontWeight: 700, letterSpacing: "0.5px", marginBottom: "16px", textTransform: "uppercase" }}>
          Step {step} / 3 — {stepTitles[step - 1]}
        </p>

        {/* Step 1 */}
        {step === 1 && (
          <div>
            <p style={{ fontWeight: 700, fontSize: "1rem", color: "#1a1a2e", marginBottom: "4px" }}>{t.step1Title}</p>
            <p style={{ fontSize: "0.8rem", color: "#888", marginBottom: "20px" }}>{t.step1Sub}</p>
            {service && (
              <div className="d-flex align-items-center gap-2 rounded-3 p-2 mb-4"
                style={{ background: "#fafcfe", border: "1.5px solid #e6eef3", width: "fit-content" }}>
                <span style={{ fontSize: "1.1rem" }}>{service.icon}</span>
                <span style={{ fontSize: "0.78rem", fontWeight: 600, color: "#1a1a2e" }}>{service.name}</span>
              </div>
            )}
            <div className="row g-2 mb-4">
              {COMPLAINT_TYPES.map((c) => (
                <div key={c.id} className="col-6">
                  <button onClick={() => setType(c.id)}
                    style={{
                      width: "100%", padding: "14px 10px", borderRadius: "12px", cursor: "pointer",
                      display: "flex", flexDirection: "column", alignItems: "center", gap: "6px",
                      background: type === c.id ? "#fff3e6" : "#fafafa",
                      border: `1.5px solid ${type === c.id ? "#e67e22" : "#e6eef3"}`,
                      transition: "all 0.15s", outline: "none"
                    }}>
                    <span style={{ fontSize: "1.5rem" }}>{c.icon}</span>
                    <span style={{ fontSize: "0.76rem", fontWeight: type === c.id ? 700 : 400, color: type === c.id ? "#e67e22" : "#555", textAlign: "center", lineHeight: 1.3 }}>
                      {isEn ? c.labelEn : c.labelRu}
                    </span>
                  </button>
                </div>
              ))}
            </div>
            <button onClick={() => setStep(2)} disabled={!type}
              style={{ width: "100%", padding: "13px", background: !type ? "#ccc" : "#e67e22", color: "white", border: "none", borderRadius: "8px", fontSize: "1rem", fontWeight: 600, cursor: !type ? "not-allowed" : "pointer", marginBottom: "10px", transition: "background 0.2s" }}
              onMouseEnter={e => { if (type) e.currentTarget.style.background = "#cf6d17"; }}
              onMouseLeave={e => { if (type) e.currentTarget.style.background = "#e67e22"; }}>
              {t.complaintNextBtn}
            </button>
            <button onClick={() => navigate(-1)}
              style={{ width: "100%", padding: "13px", background: "white", color: "#e67e22", border: "1.5px solid #e67e22", borderRadius: "8px", fontSize: "1rem", fontWeight: 600, cursor: "pointer", transition: "background 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.background = "#fff3e6"}
              onMouseLeave={e => e.currentTarget.style.background = "white"}>
              {t.complaintBackBtn}
            </button>
          </div>
        )}

        {/* Step 2 */}
        {step === 2 && (() => {
          const chosen = COMPLAINT_TYPES.find(c => c.id === type);
          return (
            <div>
              <p style={{ fontWeight: 700, fontSize: "1rem", color: "#1a1a2e", marginBottom: "4px" }}>{t.step2Title}</p>
              <p style={{ fontSize: "0.8rem", color: "#888", marginBottom: "20px" }}>{t.step2Sub}</p>
              <div className="d-flex align-items-center gap-3 rounded-3 p-3 mb-4"
                style={{ background: "#fff3e6", border: "1px solid #f0c080" }}>
                <span style={{ fontSize: "1.8rem" }}>{chosen?.icon}</span>
                <div>
                  <p style={{ fontWeight: 700, fontSize: "0.85rem", color: "#e67e22", marginBottom: "2px" }}>
                    {isEn ? chosen?.labelEn : chosen?.labelRu}
                  </p>
                  <p style={{ fontSize: "0.75rem", color: "#a04000", marginBottom: 0 }}>{t.step2Sub}</p>
                </div>
              </div>
              <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 600, color: "#1a1a2e", marginBottom: "8px" }}>
                {t.complaintDescLabel}
              </label>
              <textarea value={description} onChange={e => setDescription(e.target.value)}
                placeholder={t.complaintDescPlaceholder} rows={5}
                style={{ width: "100%", padding: "12px", border: "1.5px solid #dde", borderRadius: "10px", fontSize: "0.85rem", fontFamily: "inherit", resize: "vertical", outline: "none", marginBottom: "12px", lineHeight: 1.6 }}
                onFocus={e => e.target.style.borderColor = "#e67e22"}
                onBlur={e => e.target.style.borderColor = "#dde"} />
              <div className="rounded-3 p-3 mb-4 d-flex gap-2 align-items-start"
                style={{ background: "#fff3e6", border: "1px solid #f0c080" }}>
                <div className="d-flex align-items-center justify-content-center flex-shrink-0 rounded-circle fw-bold"
                  style={{ width: 22, height: 22, background: "#e67e22", color: "white", fontSize: "0.68rem" }}>i</div>
                <p style={{ fontSize: "0.76rem", color: "#a04000", lineHeight: 1.55, marginBottom: 0 }}>{t.complaintInfoText}</p>
              </div>
              {apiError && (
              <div style={{ background: "#fde2e2", color: "#9d1b1b", borderRadius: "12px", padding: "12px", marginBottom: "16px", fontSize: "0.88rem" }}>
                {apiError}
              </div>
            )}
            <button onClick={async () => {
                setApiError("");
                if (!serviceName || !phone || !network) {
                  setApiError(t.complaintSubmitFailed || "Unable to submit complaint. Please try again.");
                  return;
                }
                setSubmitting(true);
                try {
                  const body = {
                    phone,
                    network,
                    service_name: serviceName,
                    type,
                    description,
                    language,
                  };
                  const res = await fetch(apiUrl("/complaints"), {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(body),
                  });
                  const json = await res.json();
                  if (!res.ok || !json.success) {
                    throw new Error(json.message || t.complaintSubmitFailed || "Unable to submit complaint. Please try again.");
                  }
                  setSubmittedComplaintId(json.complaintId || json.id || null);
                  setStep(3);
                } catch (err) {
                  setApiError(err.message || t.complaintSubmitFailed || "Unable to submit complaint. Please try again.");
                } finally {
                  setSubmitting(false);
                }
              }} disabled={!description.trim() || submitting}
                style={{ width: "100%", padding: "13px", background: !description.trim() || submitting ? "#ccc" : "#e67e22", color: "white", border: "none", borderRadius: "8px", fontSize: "1rem", fontWeight: 600, cursor: !description.trim() || submitting ? "not-allowed" : "pointer", marginBottom: "10px", transition: "background 0.2s" }}
                onMouseEnter={e => { if (description.trim() && !submitting) e.currentTarget.style.background = "#cf6d17"; }}
                onMouseLeave={e => { if (description.trim() && !submitting) e.currentTarget.style.background = "#e67e22"; }}>
                {submitting ? (t.complaintSubmitting || "Submitting...") : t.complaintSubmitBtn}
              </button>
              <button onClick={() => setStep(1)}
                style={{ width: "100%", padding: "13px", background: "white", color: "#e67e22", border: "1.5px solid #e67e22", borderRadius: "8px", fontSize: "1rem", fontWeight: 600, cursor: "pointer", transition: "background 0.2s" }}
                onMouseEnter={e => e.currentTarget.style.background = "#fff3e6"}
                onMouseLeave={e => e.currentTarget.style.background = "white"}>
                {t.complaintBackBtn}
              </button>
            </div>
          );
        })()}

        {/* Step 3 */}
        {step === 3 && (
          <div className="text-center py-4">
            <div style={{ width: 76, height: 76, borderRadius: "50%", background: "#fff3e6", margin: "0 auto 16px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "2.2rem" }}>✅</div>
            <p style={{ fontWeight: 700, fontSize: "1.1rem", color: "#1a1a2e", marginBottom: "8px" }}>{t.step3Title}</p>
            <p style={{ fontSize: "0.85rem", color: "#666", marginBottom: "6px" }}>{t.complaintRegistered}</p>
            <div className="rounded-3 p-3 mb-4 d-inline-block" style={{ background: "#fff3e6", border: "1px solid #f0c080" }}>
              <p style={{ fontSize: "0.72rem", color: "#a04000", marginBottom: "2px" }}>{t.complaintIdLabel}</p>
              <p style={{ fontWeight: 700, fontSize: "1rem", color: "#e67e22", marginBottom: 0, letterSpacing: "1px" }}>#{submittedComplaintId || complaintId}</p>
            </div>
            <p style={{ fontSize: "0.82rem", color: "#888", marginBottom: "32px" }}>{t.complaintFollowUp}</p>
            <button onClick={() => navigate("/")}
              style={{ padding: "13px 36px", background: "#e67e22", color: "white", border: "none", borderRadius: "8px", fontSize: "1rem", fontWeight: 600, cursor: "pointer", transition: "background 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.background = "#cf6d17"}
              onMouseLeave={e => e.currentTarget.style.background = "#e67e22"}>
              {t.goHomeBtn2}
            </button>
          </div>
        )}

        {step < 3 && (
          <ZaraAssistant message={t.ZARAMessage} features={["Tez aur Asaan", "Safe & Secure", "24/7 Support"]} />
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Complaint;